'use client'

import { lazy, Suspense, useEffect, useRef, useState } from 'react'
import { Capacitor } from '@capacitor/core'
import {
  AdjustmentsHorizontalIcon as Settings2,
  ArrowDownTrayIcon as Download,
  ArrowPathIcon as LoaderCircle,
  ArrowPathIcon as RefreshCw,
  ChartBarSquareIcon as BarChart3,
  ClockIcon as Timer,
  SparklesIcon as Sparkles,
  Square3Stack3DIcon as Brain,
  Squares2X2Icon as LayoutGrid,
} from '@heroicons/react/24/solid'
import { gsap, useGSAP, reducedMotion } from '@/lib/gsap'
import { playSound } from '@/lib/sounds'
import { appFetch } from '@/data/api/app-fetch'
import { localDb } from '@/data/local/database'
import { getActiveIdentity } from '@/services/identity/local-identity'
import { requestFullSync, hydrateCloudSnapshot } from '@/services/sync/sync-engine'
import { MicroButton } from '@/components/ui/MotionPrimitives'
import SoundscapeDock from '@/features/soundscape/SoundscapeDock'
import { useFocusSoundscape } from '@/features/soundscape/FocusSoundscapeProvider'
const HomeView = lazy(() => import('./views/HomeView'))
const FocusView = lazy(() => import('./views/FocusView'))
const InspirationsView = lazy(() => import('./views/InspirationsView'))
const ReviewView = lazy(() => import('./views/ReviewView'))
const ClarifyView = lazy(() => import('./views/ClarifyView'))
const JournalView = lazy(() => import('./views/JournalView'))
const SettingsView = lazy(() => import('./views/SettingsView'))
const MindCanvas = lazy(() => import('./canvas/MindCanvas'))
const AllFlashcardLibrary = lazy(() => import('./review/AllFlashcardLibrary'))
const SoundscapeStudio = lazy(() => import('@/features/soundscape/SoundscapeStudio'))

interface AppShellProps {
  user: any
  view: string
}

/** Ctrl+S 保存反馈：phase 决定文案与样式，自动消失（不阻塞操作） */
interface SaveFeedback {
  phase: 'saving' | 'success' | 'error'
  message: string
}

const CENTER_TAB_ID = 'canvas'

const tabs = [
  { id: 'home', icon: LayoutGrid, label: '总览' },
  { id: 'focus', icon: Timer, label: '专注' },
  { id: CENTER_TAB_ID, icon: Brain, label: '闪念画布' },
  { id: 'clarify', icon: Sparkles, label: '解惑' },
  { id: 'journal', icon: BarChart3, label: '复盘' },
]

export default function AppShell({ user, view: initialView }: AppShellProps) {
  const { isStudioOpen } = useFocusSoundscape()
  const [view, setView] = useState(initialView)
  const [reviewInitialMode, setReviewInitialMode] = useState<'mistake-notebook' | undefined>(() => (
    new URLSearchParams(window.location.search).get('reviewMode') === 'mistake-notebook' ? 'mistake-notebook' : undefined
  ))
  const [homeRefreshKey, setHomeRefreshKey] = useState(0)
  const [totalFocusHours, setTotalFocusHours] = useState<number | null>(null)
  const [refreshing, setRefreshing] = useState(false)
  // 画布复习模式 = 完全全屏：隐藏底部 dock 栏
  const [, setCanvasFullScreen] = useState(false)
  // Ctrl+S 保存反馈（顶部悬浮条，pointer-events: none，不干扰操作）
  const [saveFeedback, setSaveFeedback] = useState<SaveFeedback | null>(null)
  const saveBusyRef = useRef(false)
  const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const tabRefs = useRef(new Map<string, HTMLButtonElement>())
  const tabbarRef = useRef<HTMLDivElement>(null)
  const indicatorRef = useRef<HTMLDivElement>(null)
  const indicatorPlaced = useRef(false)

  /** Ctrl+S 保存上传：普通实体与全部画布快照使用同一套可等待的完整同步。 */
  const saveToServer = async () => {
    if (saveBusyRef.current) return
    saveBusyRef.current = true
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current)
    setSaveFeedback({ phase: 'saving', message: '保存中…' })
    try {
      await requestFullSync()
      const identity = await getActiveIdentity()
      if (!identity.authenticated) {
        setSaveFeedback({ phase: 'success', message: '保存成功（本地模式）' })
      } else {
        const sync = await localDb.syncState.get(identity.workspaceId)
        if (sync?.status === 'error' || sync?.status === 'offline') {
          setSaveFeedback({ phase: 'error', message: sync?.errorMessage ? `保存失败：${sync.errorMessage}` : '保存失败' })
        } else {
          setSaveFeedback({ phase: 'success', message: '保存成功' })
        }
      }
    } catch (error) {
      setSaveFeedback({ phase: 'error', message: `保存失败：${error instanceof Error ? error.message : '网络错误'}` })
    } finally {
      saveBusyRef.current = false
      // 成功后稍作停留自动消失（失败多停留一会儿）
      saveTimerRef.current = setTimeout(() => setSaveFeedback(null), 2000)
    }
  }

  // 全局 Ctrl/Cmd+S：阻止浏览器默认"保存网页"，改为保存上传到服务器
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 's') {
        event.preventDefault()
        void saveToServer()
      }
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [])

  useEffect(() => () => { if (saveTimerRef.current) clearTimeout(saveTimerRef.current) }, [])

  const navigate = (v: string) => {
    if (v !== 'review') setReviewInitialMode(undefined)
    if (v === 'home') setHomeRefreshKey(value => value + 1)
    const href = v === 'home' ? '/' : `/?view=${encodeURIComponent(v)}`
    window.history.pushState({}, '', href)
    if (v !== view) playSound('nav')
    setView(v)
  }

  // 一键刷新：拉取云端最新数据（Push 本地改动 + Pull 云端增量 + 快照校准）
  const refreshData = async () => {
    if (refreshing) return
    setRefreshing(true)
    playSound('pop')
    try {
      await requestFullSync()
      // 顶部刷新 = 用户主动操作 → 强制完整校准，确保与云端完全一致
      await hydrateCloudSnapshot(true)
    } finally {
      setRefreshing(false)
      if (view === 'home') setHomeRefreshKey(value => value + 1)
    }
  }

  useEffect(() => {
    const onPopState = () => setView(new URLSearchParams(window.location.search).get('view') || 'home')
    window.addEventListener('popstate', onPopState)
    return () => window.removeEventListener('popstate', onPopState)
  }, [])

  // 应用壳布局：锁定 body 滚动，滚动只发生在 main 内容区内（顶栏/底部 tab 固定）
  useEffect(() => {
    const previous = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => { document.body.style.overflow = previous }
  }, [])

  // 顶栏累计专注统计：切换页面时刷新，保证专注完成后数字及时更新
  useEffect(() => {
    let active = true
    const loadTotalFocus = () => {
      void appFetch('/api/focus/heatmap')
        .then(response => response.json())
        .then(data => {
          if (!active) return
          const minutes = Object.values((data.minutesByDate || {}) as Record<string, number>)
            .reduce((sum, value) => sum + value, 0)
          setTotalFocusHours(Math.round(minutes / 60 * 10) / 10)
        })
        .catch(() => {})
    }
    loadTotalFocus()
    window.addEventListener('study-tool:synced', loadTotalFocus)
    window.addEventListener('study-tool:snapshot-hydrated', loadTotalFocus)
    return () => {
      active = false
      window.removeEventListener('study-tool:synced', loadTotalFocus)
      window.removeEventListener('study-tool:snapshot-hydrated', loadTotalFocus)
    }
  }, [view])

  const renderView = () => {
    switch (view) {
      case 'focus': return <FocusView user={user} />
      case 'inspirations': return <InspirationsView />
      case 'clarify': return <ClarifyView />
      case 'canvas': return <MindCanvas staticMode onClose={() => { setCanvasFullScreen(false); navigate('home') }} onFullScreenChange={setCanvasFullScreen} />
      case 'review': return <ReviewView initialMode={reviewInitialMode} />
      case 'journal': return <JournalView />
      case 'settings': return <SettingsView user={user} />
      case 'flashcards': return <AllFlashcardLibrary onClose={() => navigate('home')} backLabel="返回总览" />
      default: return (
        <HomeView
          key={homeRefreshKey}
          user={user}
          onOpenFlashcards={() => navigate('flashcards')}
          onOpenCanvas={() => navigate('canvas')}
          onOpenMistakeNotebook={() => { setReviewInitialMode('mistake-notebook'); navigate('review') }}
        />
      )
    }
  }

  const isTabActive = (id: string) => view === id || (view === 'home' && id === 'home')
  // 设置页不属于主导航,此时不激活任何 Tab、隐藏指示器
  const activeTabId = view === 'home' || tabs.some(tab => tab.id === view) ? (view === 'home' ? 'home' : view) : null

  // 底部导航滑动指示器:GSAP 补间到当前激活 Tab（v1.4.8：中间 tab 与其余一致，无独立 FAB）
  useGSAP(
    () => {
      const indicator = indicatorRef.current
      if (!indicator) return
      if (!activeTabId) {
        gsap.to(indicator, { autoAlpha: 0, duration: 0.25 })
        indicatorPlaced.current = true
        return
      }
      const active = tabRefs.current.get(activeTabId)
      if (!active) return
      const target = { x: active.offsetLeft, width: active.offsetWidth, autoAlpha: 1 }
      if (!indicatorPlaced.current || reducedMotion()) {
        gsap.set(indicator, target)
        indicatorPlaced.current = true
        return
      }
      gsap.to(indicator, { ...target, duration: 0.45, ease: 'power3.out' })
    },
    { scope: tabbarRef, dependencies: [activeTabId] },
  )

  return (
    <div className={`app-shell ${view === 'canvas' ? 'canvas-shell' : ''} ${view === 'clarify' ? 'app-shell--clarify' : ''}`}>
      {/* Ctrl+S 保存反馈：顶部悬浮，不拦截点击（pointer-events: none） */}
      {saveFeedback && (
        <div className={`save-toast save-toast-${saveFeedback.phase}`} role="status" aria-live="polite">
          {saveFeedback.phase === 'saving' && <LoaderCircle className="toolbar-spinner" />}
          <span>{saveFeedback.message}</span>
        </div>
      )}
      {/* 顶部栏：只留累计专注统计与设置入口（画布工作台模式隐藏） */}
      {view !== 'canvas' && (
      <header className="app-header" aria-hidden={isStudioOpen || undefined}>
        <MicroButton className="header-stat" onClick={() => navigate('home')} aria-label="返回学习总览">
          <img className="header-fox-logo" src="/logo-mark.png" alt="" aria-hidden="true" />
          <span>累计专注 <strong>{totalFocusHours ?? '--'}</strong> 小时</span>
        </MicroButton>

        <div className="header-actions">
          <MicroButton
            className={`header-icon-btn ${refreshing ? 'refreshing' : ''}`}
            onClick={() => void refreshData()}
            aria-label="刷新同步数据"
            title="刷新同步数据"
            disabled={refreshing}
          >
            <RefreshCw />
          </MicroButton>

          {!Capacitor.isNativePlatform() && (
          <a
            className="header-icon-btn"
            href="/download/ShanganCard.apk"
            download="闪念上岸.apk"
            aria-label="下载 Android App"
            title="下载闪念上岸 Android App"
          >
            <Download />
          </a>
          )}

          <MicroButton
            className={`header-icon-btn ${view === 'settings' ? 'active' : ''}`}
            onClick={() => navigate('settings')}
            aria-label="个人设置"
            title="个人设置"
          >
            <Settings2 />
          </MicroButton>
        </div>
      </header>
      )}

      {/* 主内容（flex:1 撑满，内容区内部滚动；顶栏与底部 tab 固定不随内容滚动）
          解惑页为贴底对话布局：无 dock 预留 padding，播放器胶囊悬浮在输入条上方 */}
      <main key={view} aria-hidden={isStudioOpen || undefined} className={view === 'canvas' ? undefined : view === 'clarify' ? 'app-main-clarify' : 'app-main-with-soundscape'} style={{ flex: 1, minHeight: 0, overflowY: 'auto', WebkitOverflowScrolling: 'touch', ...(view === 'canvas' ? { padding: 0, overflow: 'hidden' } : undefined) }}>
        <Suspense fallback={<div className="app-loading"><div className="app-loading-text">正在加载模块…</div></div>}>
          {renderView()}
        </Suspense>
      </main>

      {/* 播放器胶囊：画布全屏时隐藏；解惑页正常显示（悬浮于输入条上方，见 .app-shell--clarify 规则） */}
      {view !== 'canvas' && !isStudioOpen && <SoundscapeDock />}

      {/* 底部拟物化标签栏:复习居中 FAB 突出（进入思维画布全屏时完全隐藏） */}
      {view !== 'canvas' && (
      <nav className="tabbar" ref={tabbarRef} aria-label="主导航" aria-hidden={isStudioOpen || undefined}>
        <div className="tabbar-indicator" ref={indicatorRef} aria-hidden="true" />
        {tabs.map(item => {
          const Icon = item.icon
          return (
            <MicroButton
              key={item.id}
              ref={el => {
                if (el) tabRefs.current.set(item.id, el)
                else tabRefs.current.delete(item.id)
              }}
              className={`tabbar-item ${isTabActive(item.id) ? 'active' : ''}`}
              onClick={() => navigate(item.id)}
              aria-label={item.label}
              aria-current={isTabActive(item.id) ? 'page' : undefined}
            >
              <Icon />
              <span>{item.label}</span>
            </MicroButton>
          )
        })}
      </nav>
      )}
      {isStudioOpen && view !== 'canvas' && (
        <Suspense fallback={null}>
          <SoundscapeStudio />
        </Suspense>
      )}
    </div>
  )
}
